@if (!pageDataLoaded()) {
  <app-page-loading-state [loadingState]="loadingState"></app-page-loading-state>
}

<div class="full-width card border-0 padding-top-md" [class.hide]="!pageDataLoaded()">
  <div class="row">
    <div class="col-12 col-md-auto">
      @if (findingsCount(); as findingsCount) {
        <button
          id="export-findings-button"
          [disabled]="findingsCount === 0"
          *appHasPermissions="downloadFindingsRequiredSecurity"
          (click)="openFieldSelectorModal(findingsCount)"
          class="btn btn-outline-primary fw-bold"
        >
          {{ 'export_%_findings' | translate: findingsCount }}
        </button>
      }
    </div>
    <div class="col-12 col-md-auto mt-2 mt-md-0 ms-0 ms-md-auto">
      <button
        *appHasPermissions="tasksCreateRequiredSecurity"
        class="btn btn-primary create-task-button"
        data-e2e-id="create-task"
        [disabled]="!canCreateTask"
        (click)="createTask()"
      >
        {{ createTaskButtonLabel() }}
      </button>
    </div>
  </div>

  <div class="row margin-bottom-md margin-top-lg">
    <div class="col-md-12">
      <app-accordion-filter
        (onApply)="applyFilters()"
        (onClear)="clearAllFilters()"
        (onClose)="closeFilters()"
        [filterLabels]="filterLabels"
        [filtersApplied]="filtersApplied"
      >
        <ng-template appFilterHost></ng-template>
      </app-accordion-filter>
    </div>
  </div>

  <div class="row margin-bottom-md">
    <div class="col col-md-auto">
      <div #columnsSwitcherDropdown class="dropdown">
        <button
          class="dropdown-toggle btn btn-link fw-bold text-underline btn-focus-contrast-outline"
          data-toggle="columnSelector"
          aria-haspopup="true"
          aria-controls="columnSelector"
          id="columnSelectorToggler"
          [ngClass]="{ 'border-dark': columnSelectorOpened }"
          [attr.aria-expanded]="columnSelectorOpened"
          (click)="toggleColumnSelector()"
        >
          <svg aria-hidden="true" width="16" height="14" class="margin-right-xs">
            <use xlink:href="#fa-columns"></use>
          </svg>
          <span>{{ 'label_hide_and_show_columns' | translate }}</span>
        </button>

        <div
          class="dropdown-menu m-0 p-3 border-dark"
          [class]="{ show: columnSelectorOpened }"
          id="columnSelector"
          aria-labelledby="columnSelectorToggler"
        >
          <app-issue-table-column-selector
            [selectedColumns]="optionalColumns()"
            [excludedColumns]="excludedColumns()"
            (cancelled)="columnSelectorOpened = false"
            (selectedColumnsChange)="onSelectedColumnsChange($event)"
          >
          </app-issue-table-column-selector>
        </div>
      </div>
    </div>

    @if (displayBulkActions()) {
      <div ngbDropdown class="col-12 col-md-auto mt-3 mt-md-0 me-auto" id="bulk-actions-dropdown">
        <button
          ngbDropdownToggle
          class="btn btn-link fw-bold"
          id="bulk-actions-menu"
          [disabled]="!enableBulkActions"
          #bulkActionsButton
        >
          {{ 'bulk_actions' | translate }}
        </button>
        <div ngbDropdownMenu aria-labelledby="bulk-actions-menu" role="menu" id="bulk-actions-menu-items">
          <button
            *appHasPermissions="auditUpdateOrDeleteRequiredSecurity"
            ngbDropdownItem
            role="menuitem"
            [disabled]="!enableBulkDismiss"
            [attr.aria-hidden]="!enableBulkDismiss"
            [attr.tabindex]="!enableBulkDismiss ? -1 : null"
            (click)="openBulkDismissModal()"
          >
            {{ 'label_dismiss_button' | translate }}
          </button>
          @if (showIgnoredStatus) {
            <button
              ngbDropdownItem
              role="menuitem"
              [disabled]="!enableBulkIgnore"
              [attr.aria-hidden]="!enableBulkIgnore"
              [attr.tabindex]="!enableBulkIgnore ? -1 : null"
              (click)="onBulkIgnore()"
            >
              {{ 'label_ignore_button' | translate }}
            </button>
          }
          <button
            *appHasPermissions="bulkStatusChangeSecurity"
            ngbDropdownItem
            role="menuitem"
            [disabled]="!enableBulkStatusChange"
            [attr.aria-hidden]="!enableBulkStatusChange"
            [attr.tabindex]="!enableBulkStatusChange ? -1 : null"
            (click)="openBulkStatusChangeModal()"
          >
            {{ 'change_status' | translate }}
          </button>
        </div>
      </div>
    }

    <div class="col">
      <div class="d-flex justify-content-end">
        <app-responsive-pagination
          [(pageSize)]="pageSize"
          [entryLimits]="entryLimits"
          [collectionSize]="paginationCollectionSize"
          [(page)]="page"
        >
        </app-responsive-pagination>
      </div>
    </div>
  </div>

  @if (showSuccessAlert) {
    <div class="row margin-bottom-md margin-top-lg">
      <div class="col-md-12">
        <ngb-alert type="success" (closed)="onSuccessAlertClosed()" data-test-id="success-alert">
          {{ 'finding_deleted' | translate }}
        </ngb-alert>
      </div>
    </div>
  }

  <div class="row margin-bottom-md">
    <div class="col-md-12 d-flex flex-column">
      <app-manual-eval-findings-table
        [digitalPropertyKey]="digitalPropertyKey()"
        [selectedColumns]="selectedColumnNames()"
        [findings]="findings()"
        [evaluationStandards]="evaluationStandards()"
        [selectedFindingsMap]="selectedFindingsMap"
        [page]="page"
        [pageSize]="pageSize"
        [customSeveritiesData]="customSeveritiesData"
        [showActionsColumn]="showActionsColumn()"
        [userOrigin]="userOrigin()"
        [editPermission]="editPermission()"
        [dismissalPermission]="dismissalPermission()"
        [manualFindingFlagCreatePermission]="manualFindingFlagCreatePermission()"
        (findingsSelectionChange)="checkForLinkedTasks()"
        (findingActionCompleted)="getFindings$.next()"
      >
      </app-manual-eval-findings-table>
    </div>
  </div>

  <div class="d-flex justify-content-end">
    <app-responsive-pagination
      idSuffix="Bottom"
      [(pageSize)]="pageSize"
      [entryLimits]="entryLimits"
      [collectionSize]="paginationCollectionSize"
      [(page)]="page"
    >
    </app-responsive-pagination>
  </div>
</div>
